import { UsageLimits } from '../types';
import { analyzePdf, SCANNED_MAX_PAGES, type PdfAnalysisResult } from './fileParser';
import { captureProcessingError } from '../lib/sentry';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  errorType?: 'file_size' | 'page_limit' | 'scanned_page_limit' | 'unsupported_type';
  pdfAnalysis?: PdfAnalysisResult;
}

/**
 * Validates a file against the user's tier limits.
 * Checks file size and PDF page count (with separate cap for scanned PDFs).
 */
export const validateFile = async (
  file: File,
  limits: UsageLimits
): Promise<ValidationResult> => {
  const fileType = file.type;
  const fileName = file.name.toLowerCase();
  const isPdf = fileType === 'application/pdf' || fileName.endsWith('.pdf');
  const isDocx =
    fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileName.endsWith('.docx');

  if (!isPdf && !isDocx) {
    return {
      isValid: false,
      error: 'Unsupported file type. Please upload a PDF or DOCX file.',
      errorType: 'unsupported_type',
    };
  }

  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > limits.maxFileSizeMB) {
    return {
      isValid: false,
      error: `File size (${fileSizeMB.toFixed(1)} MB) exceeds the ${limits.maxFileSizeMB} MB limit for your plan.`,
      errorType: 'file_size',
    };
  }

  if (isPdf) {
    try {
      const pdfAnalysis = await analyzePdf(file);

      if (pdfAnalysis.isScanned && pdfAnalysis.pageCount > SCANNED_MAX_PAGES) {
        captureProcessingError(
          new Error(`Scanned PDF exceeds ${SCANNED_MAX_PAGES}-page cap`),
          {
            fileType: 'application/pdf',
            pageCount: pdfAnalysis.pageCount,
            errorStage: 'validation',
          }
        );

        return {
          isValid: false,
          error: `Your document has ${pdfAnalysis.pageCount} pages; scanned PDFs are currently capped at ${SCANNED_MAX_PAGES}. Text-based PDFs follow your plan limit.`,
          errorType: 'scanned_page_limit',
        };
      }

      if (!pdfAnalysis.isScanned && pdfAnalysis.pageCount > limits.maxPdfPages) {
        return {
          isValid: false,
          error: `PDF has ${pdfAnalysis.pageCount} pages, which exceeds the ${limits.maxPdfPages} page limit for your plan.`,
          errorType: 'page_limit',
        };
      }

      return { isValid: true, pdfAnalysis };
    } catch {
      return {
        isValid: false,
        error: 'Could not read PDF file to verify page count.',
        errorType: 'unsupported_type',
      };
    }
  }

  return { isValid: true };
};

/**
 * Formats file size for display.
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
